/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { ITaskCardProps } from './types';
import Avatar from 'react-avatar';
import { IMeetingSimple } from '../../../../../app/resource/meeting-simple/meeting-simple.types';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { ITaskSimple } from '../../../../../app/resource/task-simple/task-simple.types';
import { IUser } from '../../../../../app/resource/user/user.types';
import { openDialog } from '../../../../../app/dialog/dialog.actions';
import { Labels } from './Labels';
import { Members } from './Members';
import { meetingSimpleSelectors } from '../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { meetingsSimplePageSelectors } from '../../../../../app/page/page-storage/meetings-simple/meetings-simple.selectors';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { MdChecklist } from 'react-icons/md';
import { formatDateWithoutOrdinals } from '../../../../../utils/date.utils';

export const TaskCard: FunctionComponent<ITaskCardProps> = ({ task, meetingId, provided }) => {
  const hasChecklist = task.hasChecklist && task.hasChecklist === '1';
  const dispatch = useDispatch();

  const creator = useSelectWithParams(userSelectors.selectResourceById, task.userId);

  const formatDateWithCreator = useCallback(
    (showCreatedDate: boolean, task: ITaskSimple, defaultDueDate: string | undefined, creator: IUser | null) => {
      const date = showCreatedDate ? task.created : defaultDueDate || '0000-00-00 00:00:00';
      const formattedDate = formatDateWithoutOrdinals(moment(date));

      if (showCreatedDate) {
        const creatorName = creator ? `${creator.firstName} ${creator.lastName}` : null;
        return (
          <div className="flex flex-wrap" style={{ gap: 2 }}>
            <div className="white-space-nowrap">Created: {formattedDate}</div>
            {creatorName ? <div className="white-space-nowrap"> by {creatorName}</div> : <></>}
          </div>
        );
      }

      return <div>Due: {formattedDate}</div>;
    },
    [],
  );

  const openTrelloReplacementFormDialog = useCallback(
    (taskId: ITaskSimple['id']) => {
      dispatch(openDialog('trelloReplacementFormDialog', { taskId, meetingId }));
    },
    [dispatch, meetingId],
  );
  const defaultStartDateValue = task?.dueDate;
  const defaultDueDate = useMemo(() => {
    try {
      if (defaultStartDateValue) {
        const dateString = moment(defaultStartDateValue).format('YYYY-MM-DD');
        if (dateString === 'Invalid date') {
          //dispatch(updateTask({ ...task, dueDate: '0000-00-00 00:00:00', label: task.label ? task.label : [] }));
          return '0000-00-00 00:00:00';
        }
        return defaultStartDateValue;
      }
    } catch (error) {
      console.log(error);
      return '0000-00-00 00:00:00';
    }
  }, [defaultStartDateValue]);
  const current = moment().startOf('day');

  //Difference in number of days
  const differenceDays = moment.duration(moment(defaultDueDate, 'YYYY-MM-DD').diff(current)).asDays();
  const status = task ? task.status : '0';
  const styleDate = useMemo(() => {
    if (defaultDueDate === '0000-00-00 00:00:00') {
      return 'lightGray-bg blueGray';
    } else if (status === '1') {
      return 'lightGreen-bg green';
    } else if (differenceDays <= 2 && differenceDays >= 0) {
      return 'lightYellow-bg yellow';
    } else if (differenceDays < 0) {
      return 'lightRed-bg red';
    } else {
      return 'lightGray-bg blueGray';
    }
  }, [defaultDueDate, differenceDays, status]);

  const showCreatedDate =
    task.showInTasks === '0' || !defaultDueDate || !moment(defaultDueDate, 'YYYY-MM-DD HH:mm:ss', true).isValid();

  // const [checked, setChecked] = useState(task.status === '1');
  // const onCheck = () => {
  //   setChecked(!checked);
  // };

  // useEffect(() => {
  //   if ((task.status === '1') !== checked) {
  //     dispatch(updateTask({ label: [], ...task, status: checked ? '1' : '0' }));
  //   }
  // }, [checked, dispatch, task]);

  const filteredMeetingIds = useSelect(meetingsSimplePageSelectors.selectFilteredMeetings);
  const [filteredTask, setFilteredTask] = useState(false);
  useEffect(() => {
    setFilteredTask(filteredMeetingIds.some((id: IMeetingSimple['id']) => task.meetingIds.includes(id)));
  }, [filteredMeetingIds, task]);

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, task ? task.meetingIds[0] : '');
  const isHomePage = window.location.href.endsWith('/home') ? true : false;
  return (
    task && (
      <a
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        ref={provided.innerRef}
        className={`card board-card panel popup_button ${filteredTask === true ? 'none' : ''}`}
        onClick={() => openTrelloReplacementFormDialog(task.id)}
      >
        <div className={isHomePage ? 'card-header' : ''}>
          {meeting && isHomePage ? (
            <>
              {meeting.image?.length > 1 ? (
                <div className="small-avatar mr-05 white-bg">
                  <img src={meeting.image} alt="Test" className="img-fluid" style={{ borderRadius: '50%' }} />
                </div>
              ) : (
                <div className="menu-img">
                  <Avatar title="Review KPIs" name={meeting.title} alt="review KPIs" color={meeting.color} size="25" />
                </div>
              )}
            </>
          ) : (
            <></>
          )}
          {!!task.labels && <Labels labelIds={task.labels} taskId={task.id} />}
        </div>

        <div className="card-body">
          <div className="card-desc">{task.title}</div>
        </div>
        <div className="card-footer flex aic jcsb">
          <div className="text-left flex aic">
            <div className={`badge board-badge ${showCreatedDate ? 'blueGray' : styleDate} mr-1`}>
              {formatDateWithCreator(showCreatedDate, task, defaultDueDate, creator)}
            </div>
            {/* <div className="checklist-checkbox flex aic py-1 m-0">
              <input id="done" type="checkbox" checked={checked} onChange={onCheck} />
              <label htmlFor="done">&nbsp;</label>
            </div> */}
            {task.description && (
              <div className="blueGray mr-1 f-9" title="This card has a description">
                <i className="icon icon-hamburger" />
              </div>
            )}
            {hasChecklist && (
              <div className="blueGray mr-1 f-9" title="This card has a checklist">
                <MdChecklist className="blueGray" size={14} />
              </div>
            )}
          </div>
          {task.members && <Members membersIds={task.members} meetingId={meetingId} />}
        </div>
      </a>
    )
  );
};
